import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import adminApi from '../lib/adminApi';
import { 
  AlertTriangle, 
  UserMinus, 
  ListTodo, 
  Clock, 
  DollarSign,
  ChevronRight,
  RefreshCw,
  Loader2,
  X,
  AlertCircle,
  ArrowRight
} from 'lucide-react';
import { Button } from './ui/button';

/**
 * HealthAlertsHub - Centralized dashboard widget showing items requiring admin attention
 * Displays: Active offboardings, orphaned tasks, expiring retainers, overdue tasks
 */
export default function HealthAlertsHub({ 
  onNavigateToOffboarding,
  onNavigateToTasks,
  onNavigateToClients,
  className = ""
}) {
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.get('/api/admin/health-alerts');
      setAlerts(response.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch health alerts:', err);
      setError('Unable to load health alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    // Refresh every 5 minutes
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-amber-50 border-amber-200 text-amber-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-blue-500" />;
    }
  };

  // Loading state
  if (loading && !alerts) {
    return (
      <div className={`bg-white rounded-xl border border-charcoal-200 p-6 ${className}`} data-testid="health-alerts-hub">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
        </div>
      </div>
    );
  }

  // Error state
  if (error && !alerts) {
    return (
      <div className={`bg-white rounded-xl border border-charcoal-200 p-6 ${className}`} data-testid="health-alerts-hub">
        <div className="flex items-center justify-center gap-3 py-8 text-neutral-600">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={fetchAlerts}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const hasAlerts = alerts?.summary?.total_alerts > 0;
  const totalAlerts = alerts?.summary?.total_alerts || 0;
  const criticalCount = alerts?.summary?.critical || 0;
  const warningCount = alerts?.summary?.warning || 0;

  return (
    <div className={`bg-white rounded-xl border border-charcoal-200 overflow-hidden ${className}`} data-testid="health-alerts-hub">
      {/* Header */}
      <div className="px-5 py-4 border-b border-charcoal-100 bg-gradient-to-r from-charcoal-50 to-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${hasAlerts ? 'bg-red-100' : 'bg-green-100'}`}>
              <AlertTriangle className={`w-5 h-5 ${hasAlerts ? 'text-red-600' : 'text-green-600'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-charcoal-900">Health Alerts</h3>
              <p className="text-xs text-neutral-600">
                {hasAlerts 
                  ? `${totalAlerts} items requiring attention`
                  : 'All systems healthy'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasAlerts && (
              <div className="flex items-center gap-1.5">
                {criticalCount > 0 && (
                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                    {criticalCount} critical
                  </span>
                )}
                {warningCount > 0 && (
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                    {warningCount} warning
                  </span>
                )}
              </div>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchAlerts}
              disabled={loading}
              className="text-neutral-600 hover:text-charcoal-700"
              data-testid="refresh-alerts-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      {hasAlerts ? (
        <div className="divide-y divide-charcoal-100">
          {/* Active Offboardings */}
          {alerts.offboardings?.length > 0 && (
            <AlertSection
              icon={<UserMinus className="w-4 h-4" />}
              title="Employee Offboardings"
              count={alerts.offboardings.length}
              color="orange"
              expanded={expanded}
              data-testid="offboarding-alerts-section"
            >
              {alerts.offboardings.map((ob) => (
                <AlertItem 
                  key={ob.id}
                  severity={ob.severity}
                  title={ob.employee_name || 'Employee'}
                  subtitle={`Last day: ${ob.last_working_day} (${ob.days_remaining} days left)`}
                  badge={`${Math.round(ob.progress)}% complete`}
                  badgeColor={ob.progress >= 75 ? 'green' : ob.progress >= 50 ? 'amber' : 'red'}
                  onClick={() => onNavigateToOffboarding?.(ob.employee_id)}
                  data-testid={`offboarding-alert-${ob.id}`}
                />
              ))}
            </AlertSection>
          )}

          {/* Orphaned Tasks */}
          {alerts.orphaned_tasks?.length > 0 && (
            <AlertSection
              icon={<ListTodo className="w-4 h-4" />}
              title="Orphaned Tasks"
              count={alerts.orphaned_tasks.length}
              color="purple"
              expanded={expanded}
              data-testid="orphaned-tasks-section"
            >
              {alerts.orphaned_tasks.slice(0, 5).map((task) => (
                <AlertItem 
                  key={task.id}
                  severity={task.severity}
                  title={task.title}
                  subtitle="No assignee - needs reassignment"
                  badge={task.priority}
                  badgeColor={task.priority === 'critical' ? 'red' : task.priority === 'high' ? 'orange' : 'gray'}
                  onClick={() => onNavigateToTasks?.(task.id)}
                  data-testid={`orphaned-task-${task.id}`}
                />
              ))}
              {alerts.orphaned_tasks.length > 5 && (
                <div className="px-4 py-2 text-center">
                  <button 
                    onClick={() => onNavigateToTasks?.()}
                    className="text-sm text-pink-600 hover:text-pink-700 font-medium"
                  >
                    View all {alerts.orphaned_tasks.length} orphaned tasks
                  </button>
                </div>
              )}
            </AlertSection>
          )}

          {/* Expiring Retainers */}
          {alerts.expiring_retainers?.length > 0 && (
            <AlertSection
              icon={<DollarSign className="w-4 h-4" />}
              title="Expiring Retainers"
              count={alerts.expiring_retainers.length}
              color="green"
              expanded={expanded}
              data-testid="expiring-retainers-section"
            >
              {alerts.expiring_retainers.map((client) => (
                <AlertItem 
                  key={client.id}
                  severity={client.severity}
                  title={client.name}
                  subtitle={`Expires: ${client.end_date} (${client.days_remaining} days)`}
                  badge={`${client.currency === 'INR' ? '₹' : '$'}${client.retainer_value?.toLocaleString('en-IN') || 0}/mo`}
                  badgeColor="green"
                  onClick={() => onNavigateToClients?.(client.id)}
                  data-testid={`expiring-retainer-${client.id}`}
                />
              ))}
            </AlertSection>
          )}

          {/* Overdue Tasks */}
          {alerts.overdue_tasks?.length > 0 && (
            <AlertSection
              icon={<Clock className="w-4 h-4" />}
              title="Overdue Tasks"
              count={alerts.overdue_tasks.length}
              color="red"
              expanded={expanded}
              data-testid="overdue-tasks-section"
            >
              {alerts.overdue_tasks.slice(0, 5).map((task) => (
                <AlertItem 
                  key={task.id}
                  severity={task.severity}
                  title={task.title}
                  subtitle={`${task.days_overdue} days overdue`}
                  badge={task.priority}
                  badgeColor={task.priority === 'critical' || task.priority === 'High' ? 'red' : 'amber'}
                  onClick={() => onNavigateToTasks?.(task.id)}
                  data-testid={`overdue-task-${task.id}`}
                />
              ))}
              {alerts.overdue_tasks.length > 5 && (
                <div className="px-4 py-2 text-center">
                  <button 
                    onClick={() => onNavigateToTasks?.()}
                    className="text-sm text-pink-600 hover:text-pink-700 font-medium"
                  >
                    View all {alerts.overdue_tasks.length} overdue tasks
                  </button>
                </div>
              )}
            </AlertSection>
          )}
        </div>
      ) : (
        <div className="p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h4 className="font-medium text-charcoal-900">All Clear!</h4>
          <p className="text-sm text-neutral-600 mt-1">No urgent items requiring your attention</p>
        </div>
      )}

      {/* Footer with last refresh time */}
      {lastRefresh && (
        <div className="px-4 py-2 bg-charcoal-50 border-t border-charcoal-100">
          <p className="text-xs text-neutral-500 text-center">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * AlertSection - Collapsible section for a category of alerts
 */
function AlertSection({ icon, title, count, color, children, expanded, ...props }) {
  const [isOpen, setIsOpen] = useState(true);

  const colorMap = {
    orange: 'bg-orange-100 text-orange-700',
    purple: 'bg-purple-100 text-purple-700',
    green: 'bg-emerald-100 text-emerald-700',
    red: 'bg-red-100 text-red-700',
    blue: 'bg-blue-100 text-blue-700'
  };

  return (
    <div {...props}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-charcoal-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${colorMap[color] || colorMap.blue}`}>
            {icon}
          </div>
          <span className="font-medium text-charcoal-800 text-sm">{title}</span>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colorMap[color] || colorMap.blue}`}>
            {count}
          </span>
        </div>
        <ChevronRight className={`w-4 h-4 text-neutral-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-2 pb-2">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * AlertItem - Individual alert item with severity indicator
 */
function AlertItem({ severity, title, subtitle, badge, badgeColor, onClick, ...props }) {
  const badgeColorMap = {
    red: 'bg-red-100 text-red-700',
    orange: 'bg-orange-100 text-orange-700',
    amber: 'bg-amber-100 text-amber-700',
    green: 'bg-green-100 text-green-700',
    gray: 'bg-charcoal-100 text-charcoal-700',
    blue: 'bg-blue-100 text-blue-700'
  };

  const severityIndicator = {
    critical: 'bg-red-500',
    warning: 'bg-amber-500',
    info: 'bg-blue-500'
  };

  return (
    <button
      onClick={onClick}
      className="w-full mx-2 mb-1 p-3 rounded-xl bg-charcoal-50 hover:bg-charcoal-100 transition-colors text-left flex items-center gap-3 group"
      {...props}
    >
      {/* Severity indicator */}
      <div className={`w-1.5 h-8 rounded-full flex-shrink-0 ${severityIndicator[severity] || severityIndicator.info}`} />
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-charcoal-900 text-sm truncate">{title}</p>
        <p className="text-xs text-neutral-600 mt-0.5 truncate">{subtitle}</p>
      </div>
      
      {/* Badge */}
      {badge && (
        <span className={`px-2 py-1 text-xs font-medium rounded-xl flex-shrink-0 ${badgeColorMap[badgeColor] || badgeColorMap.gray}`}>
          {badge}
        </span>
      )}
      
      {/* Arrow */}
      <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:text-neutral-600 flex-shrink-0" />
    </button>
  );
}
